<template>
  <!-- v-if: with every card titleless there is nothing to show, and the hero layout would otherwise render one
       empty product box — `lead` is {} but the markup around it is unconditional. -->
  <div v-if="cards.length" class="sc-products" :class="`sc-products--${layout}`" :style="messageColors">

    <!-- hero: one recommendation, with the weaker matches as alternatives under it -->
    <template v-if="layout === 'hero'">
      <article class="sc-products--lead">
        <a class="sc-products--face" :href="lead.item_url" target="_blank" @click="cardClicked(lead)">
          <div class="sc-products--thumb">
            <img v-if="lead.image_url" :src="lead.image_url" alt="" loading="lazy"/>
            <span v-if="discount(lead)" class="sc-products--badge">&minus;{{discount(lead)}}&nbsp;%</span>
          </div>
          <div class="sc-products--body">
            <p v-if="lead.subtitle" class="sc-products--brand">{{lead.subtitle}}</p>
            <p class="sc-products--name">{{lead.title}}</p>
            <p class="sc-products--prices">
              <span class="sc-products--now">{{price(lead)}}</span>
              <s v-if="priceOriginal(lead)" class="sc-products--was">{{priceOriginal(lead)}}</s>
            </p>
            <p v-if="labels(lead)" class="sc-products--labels">{{labels(lead)}}</p>
            <ul v-if="options(lead)" class="sc-products--options">
              <li v-for="value in options(lead).values" :key="value">{{value}}</li>
              <li v-if="moreOptions(lead)" class="more">+{{moreOptions(lead)}}</li>
            </ul>
          </div>
        </a>
        <a v-if="cta(lead)" class="sc-products--cta" :href="lead.item_url" target="_blank"
           @click="cardClicked(lead)">{{cta(lead)}}</a>
      </article>

      <div v-if="alternatives.length" class="sc-products--alts">
        <a v-for="(card, i) in alternatives" :key="i" class="sc-products--row"
           :href="card.item_url" target="_blank" @click="cardClicked(card)">
          <div class="sc-products--thumb">
            <img v-if="card.image_url" :src="card.image_url" alt="" loading="lazy"/>
          </div>
          <div class="sc-products--body">
            <p v-if="eyebrow(card)" class="sc-products--eyebrow">{{eyebrow(card)}}</p>
            <p class="sc-products--name">{{card.title}}</p>
            <p class="sc-products--prices">
              <span class="sc-products--now">{{price(card)}}</span>
              <s v-if="priceOriginal(card)" class="sc-products--was">{{priceOriginal(card)}}</s>
              <span v-if="discount(card)" class="sc-products--off">&minus;{{discount(card)}}&nbsp;%</span>
            </p>
          </div>
        </a>
      </div>
    </template>

    <!-- rows: text gets the full width, for products the words decide -->
    <template v-else-if="layout === 'rows'">
      <a v-for="(card, i) in cards" :key="i" class="sc-products--row"
         :href="card.item_url" target="_blank" @click="cardClicked(card)">
        <div class="sc-products--thumb">
          <img v-if="card.image_url" :src="card.image_url" alt="" loading="lazy"/>
        </div>
        <div class="sc-products--body">
          <p v-if="eyebrow(card)" class="sc-products--eyebrow">{{eyebrow(card)}}</p>
          <p class="sc-products--name">{{card.title}}</p>
          <p class="sc-products--prices">
            <span class="sc-products--now">{{price(card)}}</span>
            <s v-if="priceOriginal(card)" class="sc-products--was">{{priceOriginal(card)}}</s>
            <span v-if="discount(card)" class="sc-products--off">&minus;{{discount(card)}}&nbsp;%</span>
          </p>
        </div>
      </a>
    </template>

    <!-- grid and carousel share the card; only the container differs -->
    <template v-else>
      <div class="sc-products--track" ref="track" @scroll="trackScrolled">
        <a v-for="(card, i) in cards" :key="i" class="sc-products--card"
           :href="card.item_url" target="_blank" @click="cardClicked(card)">
          <div class="sc-products--thumb">
            <img v-if="card.image_url" :src="card.image_url" alt="" loading="lazy"/>
            <span v-if="discount(card)" class="sc-products--badge">&minus;{{discount(card)}}&nbsp;%</span>
          </div>
          <div class="sc-products--body">
            <p v-if="card.subtitle" class="sc-products--brand">{{card.subtitle}}</p>
            <p class="sc-products--name">{{card.title}}</p>
            <p class="sc-products--prices">
              <span class="sc-products--now">{{price(card)}}</span>
              <s v-if="priceOriginal(card)" class="sc-products--was">{{priceOriginal(card)}}</s>
            </p>
            <p v-if="labels(card)" class="sc-products--labels">{{labels(card)}}</p>
            <ul v-if="options(card)" class="sc-products--options">
              <li v-for="value in options(card).values" :key="value">{{value}}</li>
              <li v-if="moreOptions(card)" class="more">+{{moreOptions(card)}}</li>
            </ul>
          </div>
          <span v-if="cta(card)" class="sc-products--cta">{{cta(card)}}</span>
        </a>
      </div>

      <div v-if="showLeftArrow" class="sc-message--carousel-arrow-container arrow-left" @click="scrollBy(-1)">
        <button class="sc-message--carousel-arrow"><icon-arrow-down fill="black"/></button>
      </div>
      <div v-if="showRightArrow" class="sc-message--carousel-arrow-container arrow-right" @click="scrollBy(1)">
        <button class="sc-message--carousel-arrow"><icon-arrow-down fill="black"/></button>
      </div>
    </template>
  </div>
</template>

<script>
import IconArrowDown from './../icons/icons/IconArrowDown'

// PRO-514: the product carousel, laid out the way the answer calls for. Every value is rendered straight off the
// card the engine sent — nothing here reformats a price or recomputes a discount, because the engine has the
// amounts as numbers and this component would only be guessing at a locale's separators.
//
// `layout` arrives as message metadata (`product_layout`). An unknown or missing value falls back to the
// carousel, which is what the widget has always shown, so an engine that predates this renders as before.
const LAYOUTS = ['carousel', 'rows', 'grid', 'hero']
const SCROLL_STEP = 216 // one card plus its gap

export default {
  components: { IconArrowDown },
  props: {
    data: {
      type: Array,
      required: true
    },
    messageColors: {
      type: Object,
      required: true
    },
    productLayout: {
      type: String,
      default: null
    },
    // The Amio API accepts only title/text/image_url/buttons inside a structure element (amio-chat's
    // `definitions.structureObject`, additionalProperties: false), so the engine sends everything else — brand,
    // both prices, the discount, labels, sizes, and item_url (which that schema does not allow either, despite
    // what PRO-490 assumed) — as `details` on that same element (PRO-550). Read here off `data[i].details` by
    // the parent and passed down already split out, and the merge below puts it back on the card.
    // Absent for a plain carousel, which then renders as it always did.
    productDetails: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      showLeftArrow: false,
      showRightArrow: false
    }
  },
  computed: {
    layout () {
      return LAYOUTS.includes(this.productLayout) ? this.productLayout : 'carousel'
    },
    cards () {
      // the two halves are built from one array in the same order, so index is the join
      const details = this.productDetails || []
      return this.data
        .map((card, i) => {
          if (!card) return card
          const { details: ownDetails, ...element } = card
          return details[i] ? { ...element, ...details[i] } : element
        })
        .filter(card => card && card.title)
    },
    // hero is the one layout that treats the first card differently: it is the recommendation
    lead () {
      return this.cards[0] || {}
    },
    alternatives () {
      return this.cards.slice(1)
    }
  },
  methods: {
    price (card) {
      return (card.product && card.product.price) || ''
    },
    priceOriginal (card) {
      return (card.product && card.product.price_original) || ''
    },
    discount (card) {
      return (card.product && card.product.discount_percent) || null
    },
    labels (card) {
      const labels = (card.product && card.product.labels) || []
      return labels.length ? labels.join(' · ') : ''
    },
    options (card) {
      const options = card.product && card.product.options
      return options && options.values && options.values.length ? options : null
    },
    moreOptions (card) {
      const options = this.options(card)
      return options && options.total > options.values.length ? options.total - options.values.length : 0
    },
    // brand and category on one line, so a compact row does not need two of them
    eyebrow (card) {
      const parts = []
      if (card.subtitle) parts.push(card.subtitle)
      const labels = (card.product && card.product.labels) || []
      if (labels.length) parts.push(labels[0])
      return parts.join(' · ')
    },
    cta (card) {
      const button = (card.buttons || []).find(candidate => candidate && candidate.title)
      return button ? button.title : ''
    },
    // Reported as the button press it is, so a click is recorded the same way as a carousel button today. The
    // anchor still does the navigating — the href is the one thing that must work even if this handler throws.
    cardClicked (card) {
      const button = (card.buttons || [])[0]
      if (button) this.$emit('buttonPressed', button)
    },
    trackScrolled () {
      const track = this.$refs.track
      if (!track) return
      this.showLeftArrow = track.scrollLeft > 0
      this.showRightArrow = track.scrollLeft < track.scrollWidth - track.clientWidth - 1
    },
    scrollBy (direction) {
      const track = this.$refs.track
      if (track) track.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' })
    }
  },
  mounted () {
    this.trackScrolled() // sets the initial arrow visibility
  }
}
</script>
